import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import z from "zod";
import { preloadQuery } from "@relay";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { ShoppingBag } from "lucide-react";
import { Card } from "@shadcn/ui/card";
import { ThaliaHeader } from "~/components/layout/ThaliaHeader";
import thaliaShopCard from "@assets/thalia-shop-card.webp";
import type { finishQuery } from "./__generated__/finishQuery.graphql";
import { AppHeader } from "~/components/layout/AppHeader";

export const query = graphql`
	query finishQuery($pickupId: UUID!) {
		pickup(id: $pickupId) @required(action: THROW) {
			id
			hub @required(action: THROW) {
				id
				meta {
					id
					name
					organization {
						id
						slug
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/clickncollect/finish")({
	beforeLoad: () => {
		document.title = "bacehub | Startseite";
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			pickupId: z.string(),
			snc: z.boolean().optional(),
			thalia: z.boolean().optional(),
		})
	),
	loaderDeps: ({ search: { pickupId } }) => ({ pickupId }),
	loader: async ({ context, deps }) => {
		return preloadQuery<finishQuery>(context.relayEnvironment, query, { pickupId: deps.pickupId });
	},
});

export default function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { pickup } = usePreloadedQuery<finishQuery>(query, Route.useLoaderData());
	const { snc, thalia } = Route.useSearch();
	const hubMetaName = pickup.hub.meta?.name || "";
	const isBaceOrganization = pickup.hub.meta?.organization?.slug?.toLowerCase() === "bace";
	const isLadoHub = hubMetaName.toLowerCase() === "lado";

	if (isBaceOrganization && isLadoHub) {
		return (
			<div className="flex flex-col gap-4 bg-background min-h-screen">
				<div className="-mx-6">
					<AppHeader showHelpButton={true} />
				</div>

				<main className="flex flex-col px-6 py-4 gap-6">
					<div className="flex flex-col gap-3">
						<h1 className="text-2xl font-breit">
							<T>{i18n.BACE_PICKUP_THANK_YOU_HEADLINE_1()}</T>
						</h1>
						<h2 className="text-lg font-breit text-muted-foreground">
							<T>{i18n.BACE_PICKUP_THANK_YOU_HEADLINE_2()}</T>
						</h2>
					</div>

					<p className="text-base text-foreground">
						<T>{i18n.BACE_PICKUP_THANK_YOU_TEXT()}</T>
					</p>

					<p className="text-base text-foreground">
						<T>{i18n.BACE_PICKUP_THANK_YOU_LINKS()}</T>
					</p>
				</main>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4 bg-background min-h-screen">
			{thalia ? (
				<ThaliaHeader />
			) : (
				<div className="-mx-6">
					<AppHeader showHelpButton={true} />
				</div>
			)}

			<main className="flex flex-col">
				<section className="flex flex-col px-6 py-4">
					<h1 className="text-2xl font-breit">
						<T>{thalia ? i18n.THALIA_PICKUP_THANK_YOU() : i18n.THANK_YOU_FOR_USING_BACE()}</T>
					</h1>
				</section>

				{thalia && (
					<>
						<section className="flex flex-col px-6 py-4">
							<h3 className="text-xl text-black font-breit">
								<T>{i18n.THALIA_MORE_TO_DISCOVER()}</T>
							</h3>
						</section>

						<section className="px-6">
							<a href="https://www.thalia.de/" target="_blank" rel="noopener noreferrer" className="block">
								<Card className="overflow-hidden gap-0 rounded-[14px] border py-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
									<img src={thaliaShopCard} alt={i18n.THALIA_SHOP_CARD_TITLE()} className="aspect-[3/2] w-full object-cover" />
									<div className="flex flex-col gap-2 p-6">
										<p className="text-lg font-medium text-card-foreground">
											<T>{i18n.THALIA_SHOP_CARD_TITLE()}</T>
										</p>
										<p className="text-sm text-muted-foreground">
											<T>{i18n.THALIA_SHOP_CARD_TEXT()}</T>
										</p>
									</div>
								</Card>
							</a>
						</section>
					</>
				)}

				<section className="flex flex-col px-6 py-4 mt-4">
					<h3 className="text-xl text-black font-breit">
						<T>{i18n.EXPLORE_BACE_SERVICES()}</T>
					</h3>
				</section>

				<section className="px-6 pb-8">
					<Link to="/$hubMetaName/instant-buy" params={{ hubMetaName }} className="block">
						<Card className="rounded-xl border p-6 shadow-lg cursor-pointer hover:shadow-xl transition-shadow">
							<div className="flex flex-col items-start gap-4">
								<Button size="icon">
									<ShoppingBag className="h-4 w-4" />
								</Button>
								<div className="flex flex-col gap-0.5">
									<p className="text-base font-medium tracking-tight text-card-foreground">
										<T>{i18n.INSTANT_BUY_TITLE()}</T>
									</p>
									<p className="text-sm text-muted-foreground">
										<T>{i18n.INSTANT_BUY_DESCRIPTION()}</T>
									</p>
								</div>
							</div>
						</Card>
					</Link>
				</section>
			</main>
		</div>
	);
}
